import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Rating,
  FormControlLabel,
  Switch,
  Alert,
  Paper,
  CircularProgress,
} from '@mui/material';
import FeedbackIcon from '@mui/icons-material/Feedback';
import StarIcon from '@mui/icons-material/Star';
import { useEvents } from '../../contexts/EventContext';
import { useAuth } from '../../contexts/AuthContext';
import { FeedbackFormData } from '../../types';
import api from '../../services/api';

interface FeedbackFormProps {
  eventId: string;
  eventTitle: string;
  onSuccess?: () => void;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ eventId, eventTitle, onSuccess }) => {
  const { submitFeedback, hasUserSubmittedFeedback } = useEvents();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const [formData, setFormData] = useState<Omit<FeedbackFormData, 'eventId'>>({
    rating: 0,
    enjoyedMost: '',
    improvements: '',
    additionalComments: '',
    wouldRecommend: true,
  });

  // Check if feedback was already submitted (API call for accuracy)
  useEffect(() => {
    const checkFeedbackStatus = async () => {
      if (!user) {
        setIsChecking(false);
        return;
      }

      // First check local state (fast)
      if (hasUserSubmittedFeedback(eventId, user.id)) {
        setAlreadySubmitted(true);
        setIsChecking(false);
        return;
      }

      // Then verify with API (accurate)
      try {
        const response = await api.get(`/feedback/check/${eventId}`);
        setAlreadySubmitted(response.data.submitted);
      } catch {
        // If check fails, allow submission (backend will reject if duplicate)
      } finally {
        setIsChecking(false);
      }
    };

    checkFeedbackStatus();
  }, [eventId, user, hasUserSubmittedFeedback]);

  const hasSubmitted = alreadySubmitted;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (formData.rating === 0) {
      setError('Please provide a rating');
      return;
    }

    if (!formData.enjoyedMost.trim()) {
      setError('Please tell us what you enjoyed most');
      return;
    }

    if (!formData.improvements.trim()) {
      setError('Please provide suggestions for improvements');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await submitFeedback(
        {
          eventId,
          ...formData,
        },
        user.id
      );
      setShowSuccess(true);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { error?: string } } };
      if (error.response?.status === 409) {
        setAlreadySubmitted(true);
        setError(null);
      } else {
        setError(error.response?.data?.error || 'Failed to submit feedback. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isChecking) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }

  if (hasSubmitted || showSuccess) {
    return (
      <Paper sx={{ p: 3, backgroundColor: '#e8f5e9', borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <FeedbackIcon sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
          <Typography variant="h6" fontWeight={600} color="success.main">
            Thank you for your feedback!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Your feedback helps us improve future events.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <FeedbackIcon sx={{ color: '#00b7ed' }} />
        <Typography variant="h6" fontWeight={600}>
          Share Your Feedback
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        How was your experience at <strong>{eventTitle}</strong>? Your feedback helps us improve!
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {/* Rating */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Overall Rating *
          </Typography>
          <Rating
            value={formData.rating}
            onChange={(_, value) => setFormData({ ...formData, rating: value || 0 })}
            size="large"
            emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
          />
        </Box>

        {/* What did you enjoy most */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="What did you enjoy most about the event? *"
          value={formData.enjoyedMost}
          onChange={(e) => setFormData({ ...formData, enjoyedMost: e.target.value })}
          placeholder="Tell us what made this event special for you..."
          sx={{ mb: 2 }}
        />

        {/* Improvements */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="What could we improve for future events? *"
          value={formData.improvements}
          onChange={(e) => setFormData({ ...formData, improvements: e.target.value })}
          placeholder="Share your suggestions..."
          sx={{ mb: 2 }}
        />

        {/* Additional comments */}
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Any additional comments? (Optional)"
          value={formData.additionalComments}
          onChange={(e) => setFormData({ ...formData, additionalComments: e.target.value })}
          placeholder="Anything else you'd like to share..."
          sx={{ mb: 2 }}
        />

        {/* Would recommend */}
        <FormControlLabel
          control={
            <Switch
              checked={formData.wouldRecommend}
              onChange={(e) => setFormData({ ...formData, wouldRecommend: e.target.checked })}
              color="success"
            />
          }
          label="I would recommend Team Jak events to other families"
          sx={{ mb: 3 }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isSubmitting}
          sx={{ py: 1.5 }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </Box>
    </Paper>
  );
};

export default FeedbackForm;
